import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { Layout } from 'antd';
import ModuleRoutes from '../modules/routes';
import TopHeader from './components/header/TopHeader';
import NavSide from './components/sider/NavSide';
import MobileNavDrawer from './components/sider/MobileNavDrawer';
import Footer from './components/footer/Footer';
import { useLocation } from 'react-router-dom';
import { makeRequestStateless } from '@utils/helpers';
import { getNotificationReq } from "./requests"
import { CaseCountsProvider } from './CaseCounts';
import OnHoldAlert from './components/OnHoldAlert';
import { MOBILE_BREAKPOINT, isMobileViewport } from '@theme/constants';
import { ErrorBoundary } from '@comps/components';
import "../style.css"
import "../commonstyle.css"

const { Content } = Layout;

/**
 * Resolve the current page title from the cached nav tree so the top bar
 * can show it. Falls back to nothing rather than guessing — BaseHeader
 * still renders the authoritative title inside the page.
 */
const usePageTitle = (pathname) => useMemo(() => {
  const path = pathname.replace(/^\//, '');
  try {
    const nav = JSON.parse(localStorage.getItem('navigation') || '[]');
    const walk = (items) => {
      for (const item of items) {
        if (item.route === path) return item.name;
        if (item.children && item.children.length > 0) {
          const found = walk(item.children);
          if (found) return found;
        }
      }
      return null;
    };
    const match = walk(nav);
    if (match) return match;
  } catch (e) { /* cache not populated yet */ }

  if (path === '') return 'Dashboard';

  // Walk back past record ids and other non-words, so /cases/details/12
  // reads as "Details" rather than "12".
  const words = path.split('/').filter(Boolean).filter((part) => !/^\d+$/.test(part));
  const last = words.pop() || '';
  return last.replace(/-/g, ' ').replace(/^\w/, (c) => c.toUpperCase());
}, [pathname]);

const Layouts = () => {
  const [noticationData, setNotificationData] = useState(null);
  const settings = useSelector((state) => state.auth.settings);
  const location = useLocation();
  const [mobile, setMobile] = useState(isMobileViewport);
  const [drawerOpen, setDrawerOpen] = useState(false);

  const title = usePageTitle(location.pathname);
  const siteName = (settings && settings.title) || "SmileCorrect";

  /*
   * One place owns the browser tab. Three screens used to set
   * document.title themselves and the rest set nothing, so most of the
   * app sat on the "Dashboard" baked into index.html — and the site's own
   * name never appeared in the tab at all, which is the one place a
   * renamed workspace most needs to show.
   */
  useEffect(() => {
    document.title = title ? `${title} · ${siteName}` : siteName;
  }, [title, siteName]);

  /** Only crossing the breakpoint changes anything. */
  useEffect(() => {
    let frame = null;
    const handleResize = () => {
      if (frame) return;
      frame = requestAnimationFrame(() => {
        frame = null;
        const next = window.innerWidth < MOBILE_BREAKPOINT;
        setMobile((was) => {
          if (was !== next && !next) setDrawerOpen(false);
          return next;
        });
      });
    };

    window.addEventListener("resize", handleResize);
    return () => {
      window.removeEventListener("resize", handleResize);
      if (frame) cancelAnimationFrame(frame);
    };
  }, []);

  /*
   * Stateless, and deliberately so. This request feeds one thing: the
   * unread count on the header's chat bell. It used to drive `loading`,
   * which blanks the whole content column — so every cold start replaced
   * the page the user had actually asked for with a spinner until an
   * unrelated notification count came back, and TopHeader already reads a
   * null count as zero in the meantime.
   */
  useEffect(() => {
    makeRequestStateless(getNotificationReq, null, onSuccess, Function);
  }, []);

  const onSuccess = (data) => setNotificationData(data);

  const toggleNav = useCallback(() => setDrawerOpen((open) => !open), []);

  return (
    <CaseCountsProvider>
    <OnHoldAlert />
    <Layout>
      { !mobile && <NavSide /> }
      { mobile && <MobileNavDrawer open={ drawerOpen } onClose={ () => setDrawerOpen(false) } /> }

      <Layout style={ { background: 'var(--canvas)' } }>
        <TopHeader
          notification={ noticationData }
          mobile={ mobile }
          onToggleNav={ toggleNav }
          title={ title }
        />
        <div className="maincontent conmargin">
          <Content>
            {/* Routes render straight away. Each page owns its own
                loading state and shows a skeleton in the shape of what it
                is fetching, which is a far better wait than the whole
                column being replaced. */ }
            <ErrorBoundary resetKey={ location.pathname }>
              <ModuleRoutes />
            </ErrorBoundary>
          </Content>
          <Footer />
        </div>
      </Layout>
    </Layout>
    </CaseCountsProvider>
  );
};

export default Layouts;
